import * as React from 'react'
import Snackbar from '@mui/material/Snackbar'
import { Alert, AlertColor, AlertTitle } from '@mui/material'

type SnackbarType = {
  message: string
  title?: string
  severity: AlertColor
}

const SnackbarContext = React.createContext<
  { showSnackbar: (message: string, severity?: AlertColor, title?: string) => void } | undefined
>(undefined)

export const SnackbarProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [snackbar, setSnackbar] = React.useState<SnackbarType | null>(null)

  const showSnackbar = React.useCallback((message: string, severity: AlertColor = 'success', title?: string) => {
    setSnackbar({ message, severity, title })
  }, [])

  const closeSnackbar = () => {
    setSnackbar(null)
  }

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {snackbar && (
        <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackbar !== null} autoHideDuration={
          snackbar?.severity === 'error'
            ? 6000
            : 4000
        } onClose={closeSnackbar}>
          <Alert onClose={closeSnackbar} severity={snackbar?.severity} sx={{ width: '100%' }}>
            {snackbar?.title && <AlertTitle>{snackbar?.title}</AlertTitle>}
            {snackbar?.message}
          </Alert>
        </Snackbar>
      )}
      {children}
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = () => {
  const context = React.useContext(SnackbarContext)
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}
